import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { unitValueToFormattedUnitLabels } from '@/helpers/unit-formatter/unit-formatter.utils';
import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Formatter for duration values.
 * Formats duration values in H:MM format (hours without leading zero, minutes with leading zero).
 *
 * Normalizes input units to 'sec' when units is undefined, empty, 'none', or 'duration'.
 * This ensures values stored as raw seconds or marked as 'duration' are properly transformed.
 *
 * NOTE: Precision is lost when converting to H:MM format since seconds are not displayed.
 * Converting back from duration to sec/min/hrs will not recover the original seconds component.
 *
 * @param {UnitValue} unitValue - The duration value and units to format
 * @param {string} [displayUnits] - Target display units ('duration' or 'timer')
 * @returns {FormattedUnitLabels} Formatted labels with valueLabel in H:MM format
 *
 * @example
 * formatDuration({ value: 3600, units: 'sec' }, 'duration')
 * // Returns: { valueLabel: '1:00', unitsLabel: '', valueUnitsLabel: '1:00' }
 *
 * @example
 * formatDuration({ value: 3600, units: 'duration' }, 'duration')
 * // Returns: { valueLabel: '1:00', unitsLabel: '', valueUnitsLabel: '1:00' }
 */
export const formatDuration = (
  unitValue: UnitValue,
  displayUnits?: string
): FormattedUnitLabels => {
  // Normalize units to 'sec' for values that are already in seconds:
  // - undefined/empty: no units specified, assume seconds
  // - 'none': explicitly no units, assume seconds
  // - 'duration': display-only format where underlying value is in seconds
  if (
    isNullOrUndefined(unitValue.units) ||
    unitValue.units === '' ||
    unitValue.units === SUPPORTED_UNITS.NONE ||
    unitValue.units?.toLowerCase() === SUPPORTED_UNITS.DURATION ||
    unitValue.units?.toLowerCase() === SUPPORTED_UNITS.TIMER
  ) {
    unitValue.units = SUPPORTED_UNITS.SEC;
  }

  const transformed = transformUnits(unitValue, displayUnits);

  return unitValueToFormattedUnitLabels({
    value: transformed.value,
    units: transformed.units,
  });
};

/**
 * Format a duration in seconds as a compact `3d 2h 14m` / `1h 23m` / `12m 5s`
 * / `45s` style string. Negative values are treated as their absolute value.
 *
 * Unlike {@link formatDuration} (which renders a `UnitValue` as `H:MM` through
 * the unit transform pipeline), this takes a raw seconds count and is intended
 * for human-readable elapsed/remaining spans (countdowns, event windows).
 *
 * @example
 * formatCompactDuration(3660)  // '1h 1m'
 * formatCompactDuration(90)    // '1m 30s'
 */
export const formatCompactDuration = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};
